import React from 'react';
import { Link } from 'react-router-dom';
import '../styles/TypingTestExtras.css';

const clickFeatures = [
  {
    icon: '⚡',
    title: 'Measure clicks per second',
    text: 'A CPS test shows how fast you can click in a set time. Use it to check reflexes, mouse speed, or a gaming warm-up.',
  },
  {
    icon: '🖱️',
    title: 'Test your mouse or trackpad',
    text: 'See whether your mouse, trackpad, or tapping style is holding your CPS down. Light, short clicks usually beat heavy presses.',
  },
  {
    icon: '🏆',
    title: 'Compare a real CPS score',
    text: 'Share a 5 second click test score with friends. Compare the same duration — a 1-second burst is not the same as a 5-second CPS.',
  },
  {
    icon: '🧠',
    title: 'A short focus break',
    text: 'A 5-second or 1-minute click test is a quick reset. It is not a typing test and does not need an account.',
  },
];

const renderParts = (parts) =>
  parts.map((part, index) => {
    if (typeof part === 'string') return <React.Fragment key={index}>{part}</React.Fragment>;
    return (
      <Link key={index} to={part.to}>
        {part.text}
      </Link>
    );
  });

const richClickContent = {
  1: {
    featuresTitle: 'Why take a 1 second CPS test?',
    h2: 'What a 1 second CPS burst measures',
    intro:
      'A 1 second CPS test is a peak click burst, not the standard 5 second click test. Click as fast as you can for 1.00s to see jitter or butterfly speed.',
    sections: [
      {
        h3: 'Is a 1 second CPS test the same as a 5 second click test?',
        parts: [
          'No. One second is a snapshot of your fastest possible click. For the score people usually compare, take the ',
          { to: '/5s-cps-click-test/', text: '5 second click test' },
          '.',
        ],
      },
      {
        h3: 'How the 1-second CPS burst works',
        p: 'The timer starts on your first click. Get as many clicks as you can before 1.00s ends. Your result is clicks per second for that burst only.',
      },
      {
        h3: 'A tip for a higher 1s CPS',
        p: 'Keep the mouse on a flat surface and use light clicks. This test is about instinct, not a long rhythm.',
      },
    ],
  },
  5: {
    featuresTitle: 'Why take a 5 second click test?',
    h2: 'What a 5 second click test (CPS) measures',
    intro:
      'A 5 second click test is the standard CPS score: how fast you can click for 5.00 seconds. You get total clicks, clicks per second, and a Cheetah-to-Snail rank. It is not a typing test.',
    sections: [
      {
        h3: 'Why 5 seconds is the usual CPS score',
        parts: [
          'Five seconds is long enough to find a rhythm and short enough to stay a burst. Searchers looking for a 5 sec CPS test or “how fast can you click” usually want this duration, not a 1-second peak. For stamina, use the ',
          { to: '/60s-cps-click-test/', text: '1 minute click test' },
          ' (click marathon) instead.',
        ],
      },
      {
        h3: 'How to take a 5 second CPS test',
        p: 'Get a comfortable grip. The 5-second timer starts on your first click. Keep clicking until time runs out — your CPS is total clicks divided by 5.',
      },
      {
        h3: 'What is a good CPS on a 5 second click test?',
        parts: [
          'Roughly 4–7 CPS is casual, 8–10 is good, and 11+ is excellent on a 5-second clock. Average CPS is lower on a full minute. Read ',
          { to: '/blog/what-is-a-good-cps/', text: 'what is a good CPS' },
          ' if you want the bands in one place.',
        ],
      },
      {
        h3: '5 second click test vs typing test',
        parts: [
          'CPS is mouse clicks per second. WPM and CPM are keyboard typing. If you wanted a typing test, open the ',
          { to: '/60s-cpm-typing-test/', text: '1 minute typing test' },
          ' instead.',
        ],
      },
    ],
  },
  15: {
    featuresTitle: 'Why take a 15 second click test?',
    h2: 'What a 15 second CPS test measures',
    intro:
      'A 15 second click test checks whether your clicks per second holds after the first burst. It is not a 1-second CPS test and not a typing test.',
    sections: [
      {
        h3: 'When to use 15 seconds',
        parts: [
          'Use 15s if a ',
          { to: '/5s-cps-click-test/', text: '5 second click test' },
          ' feels too short and a full minute feels too long. It is a stamina check, not a peak jitter score.',
        ],
      },
      {
        h3: 'How this 15s CPS test works',
        p: 'The timer starts on your first click. Keep a fast, steady pace for the full 15 seconds. Your CPS is total clicks divided by 15.',
      },
    ],
  },
  30: {
    featuresTitle: 'Why take a 30 second click test?',
    h2: 'What a 30 second click test measures',
    intro:
      'A 30 second click test is a mouse stamina CPS score. It shows whether you can keep clicking fast after the opening burst.',
    sections: [
      {
        h3: '30 seconds vs 5 seconds',
        parts: [
          'A ',
          { to: '/5s-cps-click-test/', text: '5 second click test' },
          ' is the standard CPS people quote. Thirty seconds is for endurance. Compare the same duration when you compete with friends.',
        ],
      },
      {
        h3: 'How to run the 30s CPS test',
        p: 'Start on the first click and hold a pace you can keep. Starting at max speed and fading usually scores worse than a steady rhythm.',
      },
    ],
  },
  60: {
    featuresTitle: 'Why take a 1 minute click test?',
    h2: 'What a click marathon (1 minute click test) measures',
    intro:
      'A click marathon is a 1 minute click test: total clicks and average CPS over 60 seconds. It is stamina, not a 1-second burst and not a typing test.',
    sections: [
      {
        h3: 'Click marathon vs 5 second CPS',
        parts: [
          'Quote a ',
          { to: '/5s-cps-click-test/', text: '5 second click test' },
          ' for a normal CPS score. Use this 1 minute click test when you want a click marathon that survives more than a few seconds. Average CPS here runs lower than a 5-second burst.',
        ],
      },
      {
        h3: 'How the 1-minute CPS test works',
        p: 'The clock starts on your first click. Pace yourself for the full 60 seconds. Your CPS is total clicks divided by 60.',
      },
    ],
  },
};

export default function ClickTestExtras({ duration = 5 }) {
  const content = richClickContent[duration] || richClickContent[5];
  return (
    <>
      <div className="features-wrapper">
        <section className="features-section">
          <h2>{content.featuresTitle}</h2>
          <div className="features-container">
            {clickFeatures.map((feature) => (
              <div className="feature-box" key={feature.title}>
                <span style={{ fontSize: '2em', marginRight: 16 }}>{feature.icon}</span>
                <p>
                  <strong>{feature.title}:</strong> {feature.text}
                </p>
              </div>
            ))}
          </div>
        </section>
      </div>

      <div className="wrapper-intro">
        <h2>{content.h2}</h2>
        <p className="intro">{content.intro}</p>
        {content.sections.map((section) => (
          <div key={section.h3}>
            <h3>{section.h3}</h3>
            {section.parts ? <p>{renderParts(section.parts)}</p> : <p>{section.p}</p>}
          </div>
        ))}
      </div>
    </>
  );
}
